import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { StatCard } from '../components/StatCard';
import OnboardingWidget from '../components/OnboardingWidget';
import api from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { Home, FileText, CreditCard, Wrench, AlertCircle } from 'lucide-react';

export default function TenantDashboard() {
  const [stats, setStats] = useState({
    applications: 0,
    activeTenancies: 0,
    maintenanceRequests: 0,
    nextPayment: null
  });
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [applications, tenancies, maintenance] = await Promise.all([
        api.get('/applications'),
        api.get('/tenancies'),
        api.get('/maintenance')
      ]);

      setStats({
        applications: applications.data.length,
        activeTenancies: tenancies.data.filter(t => t.status === 'active').length,
        maintenanceRequests: maintenance.data.filter(m => m.status !== 'completed').length,
        nextPayment: null
      });
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl px-4 sm:px-6">
        {/* Onboarding Widget */}
        <OnboardingWidget userRole="tenant" />

        <div className="mb-8">
          <h1 className="text-2xl sm:text-4xl font-display font-bold text-slate-900 mb-2">Welcome Back!</h1>
          <p className="text-base sm:text-lg text-slate-600">Here&apos;s what&apos;s happening with your tenancy</p>
        </div>

        {/* Stats Grid - Responsive */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
          <StatCard
            title="Active Applications"
            value={stats.applications}
            icon={FileText}
            data-testid="stat-applications"
          />
          <StatCard
            title="Active Tenancies"
            value={stats.activeTenancies}
            icon={Home}
            data-testid="stat-tenancies"
          />
          <StatCard
            title="Maintenance Requests"
            value={stats.maintenanceRequests}
            icon={Wrench}
            data-testid="stat-maintenance"
          />
          <StatCard
            title="Next Payment"
            value={stats.nextPayment ? formatCurrency(stats.nextPayment) : 'N/A'}
            icon={CreditCard}
            data-testid="stat-payment"
          />
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl p-8 border border-slate-200 mb-8">
          <h2 className="text-2xl font-display font-bold mb-6">Quick Actions</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <Link 
              to="/properties" 
              className="p-6 border-2 border-slate-200 rounded-lg hover:border-primary hover:shadow-md transition-all group"
              data-testid="action-search-properties"
            >
              <Home className="w-8 h-8 text-primary mb-3 group-hover:scale-110 transition-transform" />
              <h3 className="font-semibold text-lg mb-2">Search Properties</h3>
              <p className="text-sm text-slate-600">Find your next home</p>
            </Link>
            <Link 
              to="/tenant/maintenance" 
              className="p-6 border-2 border-slate-200 rounded-lg hover:border-primary hover:shadow-md transition-all group"
              data-testid="action-report-issue"
            >
              <Wrench className="w-8 h-8 text-accent mb-3 group-hover:scale-110 transition-transform" />
              <h3 className="font-semibold text-lg mb-2">Report Issue</h3>
              <p className="text-sm text-slate-600">Submit maintenance request</p>
            </Link>
            <Link 
              to="/tenant/payments" 
              className="p-6 border-2 border-slate-200 rounded-lg hover:border-primary hover:shadow-md transition-all group"
              data-testid="action-view-payments"
            >
              <CreditCard className="w-8 h-8 text-secondary mb-3 group-hover:scale-110 transition-transform" />
              <h3 className="font-semibold text-lg mb-2">View Payments</h3>
              <p className="text-sm text-slate-600">Check payment history</p>
            </Link>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-xl p-8 border border-slate-200">
          <h2 className="text-2xl font-display font-bold mb-6">Recent Activity</h2>
          {recentActivity.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No recent activity</p>
            </div>
          ) : (
            <div className="space-y-4">
              {recentActivity.map((activity, index) => (
                <div key={index} className="flex items-center gap-4 p-4 border border-slate-100 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium">{activity.title}</p>
                    <p className="text-sm text-slate-600">{formatDate(activity.date)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}